<template>
  <v-container fluid class="user-edit-page">
    <div class="user-edit-header">
      <div>
        <div class="user-edit-eyebrow">Administration</div>
        <h1>Edit user</h1>
        <p>Manage profile information, account access, and permissions.</p>
      </div>
      <div class="user-edit-actions">
        <v-btn text :disabled="loading" @click="cancel">Cancel</v-btn>
        <v-btn color="primary" depressed :loading="loading" @click="save">
          <v-icon left size="18">mdi-content-save-outline</v-icon>
          Save changes
        </v-btn>
      </div>
    </div>

    <v-skeleton-loader v-if="isLoading" type="card, article" />

    <template v-else>
      <v-card elevation="0" class="user-profile-card">
        <v-card-text class="user-profile-card__body">
          <div class="user-avatar-wrap">
            <v-avatar size="112" class="user-avatar">
              <v-img :src="imageUrl || defaultUserImage" alt="User profile photo" />
            </v-avatar>
            <div class="user-photo-actions">
              <v-btn small outlined color="primary" @click="pickFile">
                <v-icon left size="17">mdi-camera-outline</v-icon>
                Change photo
              </v-btn>
              <v-btn v-if="imageUrl" small text color="error" @click="removeImage">
                Remove
              </v-btn>
            </div>
            <input ref="image" type="file" class="d-none" accept="image/*" @change="onFilePicked" />
          </div>

          <div class="user-profile-summary">
            <div class="user-profile-summary__title">
              <h2>{{ User.name || "User profile" }}</h2>
              <v-chip v-if="User.superAdmin" small color="amber lighten-4" text-color="amber darken-4">
                <v-icon left small>mdi-crown</v-icon>Super admin
              </v-chip>
              <v-chip v-else-if="User.isAdmin" small color="green lighten-5" text-color="primary">
                Admin
              </v-chip>
              <v-chip v-else small color="grey lighten-4">User</v-chip>
            </div>
            <p>{{ User.email }}</p>
            <div v-if="currentUser.superAdmin" class="mt-3">
              <ChangePassword :UserId="User.id" />
            </div>
          </div>

          <div v-if="!User.superAdmin" class="user-account-status">
            <span>Account status</span>
            <v-switch v-model="User.active" inset hide-details :label="User.active ? 'Active' : 'Inactive'" />
          </div>
        </v-card-text>
      </v-card>

      <v-row class="mt-1">
        <v-col cols="12" :lg="showRights ? 6 : 12">
          <v-card elevation="0" class="user-section-card">
            <v-card-text>
              <div class="user-section-heading">
                <span><v-icon color="primary" size="20">mdi-account-outline</v-icon></span>
                <div>
                  <h2>Personal information</h2>
                  <p>Contact and login details for this account.</p>
                </div>
              </div>

              <v-row>
                <v-col cols="12">
                  <v-text-field v-model.trim="User.name" :error-messages="nameErrors" label="Full name" outlined dense :hide-details="nameErrors.length === 0" @blur="$v.User.name.$touch()" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model.trim="User.email" :error-messages="emailErrors" label="Login email" outlined dense :hide-details="emailErrors.length === 0" @blur="$v.User.email.$touch()" />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model.trim="User.phoneNumber" label="Phone number" outlined dense hide-details />
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model.trim="User.address" label="Address" outlined dense hide-details />
                </v-col>
                <v-col cols="12" sm="5">
                  <v-text-field v-model.trim="User.city" label="City" outlined dense hide-details />
                </v-col>
                <v-col cols="6" sm="3">
                  <v-text-field v-model.trim="User.state" label="State" outlined dense hide-details />
                </v-col>
                <v-col cols="6" sm="4">
                  <v-text-field v-model.trim="User.zip" label="ZIP code" outlined dense hide-details />
                </v-col>
              </v-row>

              <div v-if="!User.superAdmin && currentUser.isAdmin" class="user-admin-setting">
                <div>
                  <strong>Administrator access</strong>
                  <span>Administrators receive access to all portal features.</span>
                </div>
                <v-switch v-model="User.isAdmin" inset hide-details />
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col v-if="showRights" cols="12" lg="6">
          <v-card elevation="0" class="user-section-card user-rights-card">
            <v-card-text>
              <div class="user-section-heading user-section-heading--actions">
                <span><v-icon color="primary" size="20">mdi-shield-key-outline</v-icon></span>
                <div>
                  <h2>User permissions</h2>
                  <p>{{ selected.length }} of {{ rights.length }} permissions selected.</p>
                </div>
                <v-btn small text color="primary" @click="allRights">Select all</v-btn>
              </div>
              <v-text-field v-model="search" outlined prepend-inner-icon="mdi-magnify" dense label="Search permissions" single-line hide-details clearable class="mb-4" />
              <v-data-table v-model="selected" :headers="headers" :items="rights" :search="search" item-key="name" show-select class="user-rights-table" :items-per-page="10" />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <div class="user-edit-mobile-actions">
        <v-btn text :disabled="loading" @click="cancel">Cancel</v-btn>
        <v-btn color="primary" depressed :loading="loading" @click="save">Save changes</v-btn>
      </div>
    </template>
  </v-container>
</template>
<script>
import { mapGetters } from "vuex";
import userService from "../service.js";
import ChangePassword from "./ChangePassword";

const { validationMixin } = require("vuelidate");
const { required, minLength, email } = require("vuelidate/lib/validators");

export default {
  name: "create-User",
  data() {
    return {
      loading: false,
      defaultUserImage: require("@/assets/images/user.jpeg"),
      search: null,
      selected: [],
      imageName: "",
      imageUrl: "",
      imageFile: "",
      isLoading: true,
      headers: [
        {
          text: "Access",
          align: "start",
          sortable: false,
          value: "name",
        },
        { text: "Type", value: "Type.name" },
      ],
      User: {
        name: null,
        isAdmin: false,
        phoneNumber: null,
        email: null,
        address: null,
        city: null,
        state: null,
        zip: null,
        active: true,
        userRights: null,
        imageUrl: null,
        image: null,
      },
      rights: [],
    };
  },
  // watch: {
  //   imageUrl(val) {
  //     this.User.image = val;
  //   }
  // },
  components: {
    ChangePassword,
  },
  async created() {
    try {
      await this.getAllUserRights();
      await this.loadUser();
    } catch (error) {
      this.$swal(
        "Unable to load user",
        (error && error.data && error.data.message) || "Please try again.",
        "error"
      );
    } finally {
      this.isLoading = false;
    }
  },
  mixins: [validationMixin],
  validations: {
    User: {
      name: {
        required,
        minLength: minLength(2),
      },
      email: {
        required,
        email,
      },
    },
  },
  computed: {
    ...mapGetters("global", ["currentUser"]),
    showRights() {
      return !this.User.superAdmin && !this.User.isAdmin;
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.User.name.$dirty) return errors;
      if (!this.$v.User.name.required)
        errors.push(this.$t("validations.fieldIsRequired"));
      if (!this.$v.User.name.minLength)
        errors.push("Name must be least 2 character long");
      return errors;
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.User.email.$dirty) return errors;
      if (!this.$v.User.email.required)
        errors.push(this.$t("validations.fieldIsRequired"));
      if (!this.$v.User.email.email)
        errors.push(this.$t("validations.emailIsNotValid"));
      return errors;
    },
    assignedRights() {
      var temp = this.rights.filter((rights) => rights.assigned == true);
      return temp.length;
    },
    progress() {
      return (this.assignedRights / this.rights.length) * 100;
    },
    unassignedRights() {
      return this.rights.length - this.assignedRights;
    },
  },
  methods: {
    allRights() {
      this.selected = [];
      this.selected = this.rights;
    },
    async loadUser() {
      if (!this.$route.params || !this.$route.params.id)
        return this.$router.push("/user/list");
      const id = this.$route.params.id;
      return userService.getById(id).then((response) => {
        this.User = response.data;
        if (this.User.imageUrl) this.imageUrl = this.User.imageUrl;
        this.setPreAssignedRights();
      });
    },
    setPreAssignedRights() {
      this.selected = [];
      const rights = String(this.User.userRights || "")
        .split(",")
        .filter(Boolean);
      rights.map((x) => {
        this.rights.map((y) => {
          if (y.id == x) {
            this.selected.push(y);
          }
        });
      });
    },
    pickFile() {
      this.$refs.image.click();
    },
    removeImage() {
      this.imageName = "";
      this.imageFile = "";
      this.imageUrl = "";
      this.User.image = null;
      this.User.imageUrl = null;
      if (this.$refs.image) this.$refs.image.value = "";
    },
    onFilePicked(e) {
      const files = e.target.files;
      if (files[0] !== undefined) {
        this.imageName = files[0].name;
        if (this.imageName.lastIndexOf(".") <= 0) {
          return;
        }
        const fr = new FileReader();
        fr.readAsDataURL(files[0]);
        fr.addEventListener("load", () => {
          this.imageUrl = fr.result;
          this.User.image = this.imageUrl;
          this.imageFile = files[0]; // this is an image file that can be sent to server..
        });
      } else {
        this.imageName = "";
        this.imageFile = "";
        this.imageUrl = "";
      }
    },
    cancel() {
      this.$router.go(-1);
    },
    getAllUserRights() {
      return userService.getAllUserRights().then((result) => {
        this.rights = result.data;
      });
    },
    save() {
      const newRights = [];
      this.selected.map((x) => {
        newRights.push(x.id);
      });
      this.User.userRights = newRights.toString();
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
      } else {
        this.loading = true;
        delete this.User.password;
        return userService
          .update(this.User.id, this.User)
          .then((response) => {
            const updatedUser = { ...(response.data || this.User) };
            delete updatedUser.password;
            delete updatedUser.resetToken;

            this.User = updatedUser;
            this.imageUrl = updatedUser.imageUrl || "";
            if (this.currentUser.id === updatedUser.id) {
              this.$store.commit("global/initUser", {
                ...this.currentUser,
                ...updatedUser,
              });
            }
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "success",
                title: "User was updated!",
              });
            this.loading = false;
          })
          .catch((err) => {
            this.loading = false;
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "error",
                title:
                  (err && err.data && err.data.message) ||
                  "Unable to update user",
              });
          });
      }
    },
  },
};
</script>

<style scoped>
.user-edit-page {
  min-height: 100%;
  padding: 30px 36px 48px;
  background: #f6f8f6;
}

.user-edit-header,
.user-edit-actions,
.user-profile-card__body,
.user-profile-summary__title,
.user-photo-actions,
.user-admin-setting,
.user-section-heading {
  display: flex;
  align-items: center;
}

.user-edit-header {
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 22px;
}

.user-edit-eyebrow {
  color: var(--v-primary-base);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.user-edit-header h1 {
  margin: 4px 0 3px;
  color: #172033;
  font-size: 29px;
  line-height: 1.2;
  letter-spacing: -0.025em;
}

.user-edit-header p,
.user-section-heading p,
.user-profile-summary p {
  margin: 0;
  color: #728095;
  font-size: 13px;
}

.user-edit-actions,
.user-photo-actions {
  gap: 8px;
}

.user-edit-actions .v-btn,
.user-photo-actions .v-btn,
.user-edit-mobile-actions .v-btn {
  border-radius: 9px;
  text-transform: none;
  letter-spacing: 0;
}

.user-profile-card,
.user-section-card {
  overflow: hidden;
  border: 1px solid #e3e9e3;
  border-radius: 13px;
  background: #fff;
}

.user-profile-card__body {
  gap: 24px;
  padding: 24px 28px;
}

.user-avatar-wrap {
  display: flex;
  align-items: center;
  gap: 16px;
}

.user-avatar {
  flex: 0 0 auto;
  border: 4px solid #edf6ee;
  background: #eef3ef;
}

.user-photo-actions {
  flex-direction: column;
  align-items: flex-start;
}

.user-profile-summary {
  min-width: 0;
  flex: 1;
}

.user-profile-summary__title {
  flex-wrap: wrap;
  gap: 10px;
}

.user-profile-summary h2 {
  margin: 0;
  color: #25342b;
  font-size: 23px;
}

.user-profile-summary p {
  margin-top: 4px;
}

.user-account-status {
  min-width: 135px;
  padding-left: 24px;
  border-left: 1px solid #e6ebe7;
}

.user-account-status > span {
  display: block;
  color: #7a867e;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.user-account-status .v-input {
  margin-top: 5px;
}

.user-section-card {
  height: 100%;
}

.user-section-card > .v-card__text {
  padding: 22px 24px;
}

.user-section-heading {
  gap: 12px;
  margin-bottom: 22px;
}

.user-section-heading > span {
  display: grid;
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  place-items: center;
  border-radius: 10px;
  background: #eef7ef;
}

.user-section-heading h2 {
  margin: 0 0 2px;
  color: #2d4034;
  font-size: 17px;
}

.user-section-heading--actions > div {
  min-width: 0;
  flex: 1;
}

.user-admin-setting {
  justify-content: space-between;
  gap: 20px;
  margin-top: 6px;
  padding: 16px 18px;
  border: 1px solid #e4eae5;
  border-radius: 10px;
  background: #fafcfb;
}

.user-admin-setting strong,
.user-admin-setting span {
  display: block;
}

.user-admin-setting strong {
  color: #35483b;
  font-size: 13px;
}

.user-admin-setting span {
  margin-top: 2px;
  color: #7a867e;
  font-size: 11px;
}

.user-edit-page >>> .v-input__slot {
  border-radius: 8px;
}

.user-rights-table {
  border: 1px solid #e6ebe7;
  border-radius: 9px;
}

.user-edit-mobile-actions {
  display: none;
}

@media (max-width: 760px) {
  .user-edit-page {
    padding: 20px 12px 88px;
  }

  .user-edit-header {
    align-items: flex-start;
  }

  .user-edit-header h1 {
    font-size: 25px;
  }

  .user-edit-header > .user-edit-actions {
    display: none;
  }

  .user-profile-card__body {
    align-items: flex-start;
    padding: 20px 16px;
    flex-direction: column;
  }

  .user-avatar-wrap {
    width: 100%;
  }

  .user-avatar {
    width: 86px !important;
    height: 86px !important;
  }

  .user-account-status {
    width: 100%;
    padding: 14px 0 0;
    border-top: 1px solid #e6ebe7;
    border-left: 0;
  }

  .user-section-card > .v-card__text {
    padding: 19px 16px;
  }

  .user-section-heading--actions {
    flex-wrap: wrap;
  }

  .user-section-heading--actions .v-btn {
    margin-left: 50px;
  }

  .user-edit-mobile-actions {
    position: fixed;
    z-index: 4;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 16px;
    border-top: 1px solid #e1e7e3;
    background: #fff;
    box-shadow: 0 -5px 14px rgba(30, 50, 32, 0.07);
  }
}
</style>
